import React, { useMemo, useEffect, memo, type JSX } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@core-ui/Components/ui/card';
import { useTranslation } from 'react-i18next';
import { logInfo } from '@lib/logger/logInfo';
import { logWarning } from '@lib/logger/logWarning';
import clsx from 'clsx';

interface IntentCardProps {
  speech?: string | null;
  title?: string;
  font?: 'mono' | 'sans';
  isTransferred: boolean;
  activeVoicemail: boolean;
  isSDR?: boolean;
}

const normalizeSpeech = (value: string | null | undefined): string =>
  typeof value === 'string' ? value.trim() : '';

let hasWarnedNonStringSpeech = false;

const IntentCardComponent = ({ speech, title, font = 'mono', isTransferred, activeVoicemail, isSDR = false }: IntentCardProps): JSX.Element => {
  const { t } = useTranslation();

  const text = useMemo(() => normalizeSpeech(speech), [speech]);
  const hasContent = text.length > 0;

  const paragraphs = useMemo<string[]>(() => {
    if (!hasContent) {
      return [];
    }
    return text
      .split(/\n{2,}/g)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
  }, [hasContent, text]);

  useEffect(() => {
    if (speech != null && typeof speech !== 'string' && !hasWarnedNonStringSpeech) {
      hasWarnedNonStringSpeech = true;
      void logWarning('IntentCard received a non-string speech; normalizing to empty', 'IntentCard/propType');
    } else {
      void logInfo('IntentCard render', 'IntentCard/render', { hasContent });
    }
  }, [speech, hasContent]);

  return (
    <Card className={clsx(
      'bg-card rounded-2xl shadow-sm border border-border col-span-1 md:col-span-1 h-full flex flex-col min-h-0 overflow-hidden',
      isTransferred && 'border-(--ring)',
      !isSDR && isTransferred && 'max-h-[550px]',
      activeVoicemail && 'border-(--purple)'
    )}>
      <CardHeader className="shrink-0 px-3 md:px-4 py-1.5 md:py-2">
        <CardTitle className="text-sm md:text-base lg:text-lg">{title ?? t('dialer.intent.title', 'Intent')}</CardTitle>
      </CardHeader>

      <CardContent className="flex-1 overflow-y-auto px-3 md:px-4 pt-0 pb-3 md:pb-4 break-all">
        {hasContent ? (
          <div
            className={[
              font === 'mono' ? 'font-mono' : 'font-sans',
              'text-sm md:text-base lg:text-lg text-foreground whitespace-pre-wrap leading-relaxed space-y-3 md:space-y-4 lg:space-y-5',
              'scrollbar-thin scrollbar-thumb-muted-foreground/30 scrollbar-track-transparent',
            ].join(' ')}
          >
            {paragraphs.length > 0
              ? paragraphs.map((p, idx) => <p key={`${idx}-${p.slice(0, 16)}`}>{p}</p>)
              : <p>{text}</p>}
          </div>
        ) : (
          <span className="text-sm md:text-base lg:text-lg text-muted-foreground">
            {t('dialer.intent.empty', 'No intent available for this session.')}
          </span>
        )}
      </CardContent>
    </Card>
  );
};

const IntentCard = memo(IntentCardComponent);
IntentCard.displayName = 'IntentCard';

export default IntentCard;
